import { QUALIFICATIONS_CATEGORIES } from '../../shared/constants/qualification-categories';
import {
  Criteria,
  NestedQuota,
  QuotaPayload,
} from '../../shared/interfaces/quota.interface';
import { Supplier } from '../../shared/interfaces/survey.interface';
import { RandomStringGenerator } from '../../shared/utils/random-string-generator';

interface QuotaPayloadMappingHandle {
  map(): QuotaPayload[];
}

export class QuotaPayloadMapper {
  public fromNesting(quotas: NestedQuota[], surveyAllocation: number) {
    return new FromNestingQuotaPayloadMapper(quotas, surveyAllocation);
  }
}

class FromNestingQuotaPayloadMapper implements QuotaPayloadMappingHandle {
  constructor(
    private readonly _nestedQuotas: NestedQuota[] = [],
    private readonly _surveyAllocation: number
  ) {}

  public get nestedQuotas(): NestedQuota[] {
    return this._nestedQuotas || [];
  }

  public map(): QuotaPayload[] {
    return this._nestedQuotas.map((nestedQuota) => this._each(nestedQuota));
  }

  private _each(nestedQuota: NestedQuota): QuotaPayload {
    const {
      flexible,
      percentage,
      maximum,
      minimum,
      individual_quotas,
      quota_title,
      group_key,
    } = nestedQuota;
    return {
      buyer_quota_id: this._id(nestedQuota),
      required_count: this._count(nestedQuota),
      criteria: this._criteria(nestedQuota),
      isNested: true,
      flexible,
      percentage,
      maximum,
      minimum,
      max: maximum,
      min: minimum,
      suppliers: this._suppliers(nestedQuota),
      quota_category: this._category(nestedQuota),
      individual_quotas,
      quota_title,
      current_target: this._currentTarget(nestedQuota),
      group_key,
    };
  }

  private _id(nestedQuota: NestedQuota): string {
    return nestedQuota.buyer_quota_id || new RandomStringGenerator().generate();
  }

  private _count(nestedQuota: NestedQuota): number {
    return nestedQuota.number || nestedQuota.required_count || 0;
  }

  private _criteria(nestedQuota: NestedQuota): Criteria[] {
    return nestedQuota.criteria.map((criterion) => {
      const originalAllocation = this._originalAllocation(criterion);
      return {
        ...criterion,
        original_number: originalAllocation,
        original_minimum: originalAllocation,
        original_maximum: originalAllocation,
        original_required_count: originalAllocation,
      };
    });
  }

  private _originalAllocation(criterion: Criteria) {
    return Math.round(
      (Number(criterion.original_percentage) * this._surveyAllocation) / 100
    );
  }

  private _suppliers(nestedQuota: NestedQuota): Supplier[] {
    return nestedQuota.suppliers || [];
  }

  private _category(nestedQuota: NestedQuota): string {
    return nestedQuota.quota_category ?? QUALIFICATIONS_CATEGORIES.NESTED;
  }

  private _currentTarget(nestedQuota: NestedQuota): number {
    return nestedQuota.current_target || nestedQuota.number || 0;
  }
}
